import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { FormControl, FormGroup } from '@angular/forms';
import { AssessmentCriteria, AssessmentSettings, Course } from '@shared/models';
import { CoursesAPIService } from '@shared/services/coursesApi.service';
import { from } from 'rxjs';
import { filter, switchMap, mergeMap } from 'rxjs/operators';
import { ProgramReportingSessionLogComponent } from '@app/program/program-reporting/program-reporting-session-log/program-reporting-session-log.component';
import { AssessmentReportDialogComponent } from '@app/main/analytics/assessment/assessment-report-dialog/assessment-report-dialog.component';

@Component({
  selector: 'assessment-edit',
  templateUrl: './assessment-edit.component.html',
  styleUrls: ['./assessment-edit.component.scss']
})
export class AssessmentEditComponent implements OnInit, OnChanges {
  @Input() course: Course;
  @Output() courseChanged = new EventEmitter<Course>();
  assessment: AssessmentSettings;
  assessmentGeneralForm: FormGroup;
  criteriaListForm: FormGroup;
  editionEnabled: boolean;
  newCriteriaTitle: string;

  constructor(private _courseApiService: CoursesAPIService, private _dialog: MatDialog) {
    this.assessmentGeneralForm = new FormGroup({
      enabled: new FormControl(true),
      title: new FormControl('', { updateOn: 'blur' }),
      description: new FormControl('', { updateOn: 'blur' }),
      minScore: new FormControl(1),
      maxScore: new FormControl(5)
    });
  }

  ngOnInit() {
    this.assessmentGeneralForm.valueChanges
      .pipe(
        mergeMap(() => from(Object.entries(this.assessmentGeneralForm.controls))),
        filter(([label, control]: [string, FormControl]) => control.dirty),
        switchMap(([label, control]: [string, FormControl]) => {
          control.markAsPristine();
          const path = `information.assessment.${label}`;
          return this._courseApiService.patchCourse(this.course.id, { op: 'replace', path, value: control.value });
        })
      )
      .subscribe((course) => {
        this.courseChanged.emit(course);
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.course && !!changes.course.currentValue) {
      this.assessment = this.course.information.assessment;
      if (!this.assessment) {
        this.assessment = new AssessmentSettings('', '', []);
      }
      if (!this.assessment.criterias) {
        this.assessment.criterias = [];
      }
      this.assessmentGeneralForm.reset(this.assessment, { emitEvent: false });
      this.toggleAssessment(this.assessmentGeneralForm.controls.enabled.value);
    }
  }
  /** This function is used as ngFor trackBy, in order to avoid the all list reload/rerender at any nested attribute change */
  trackByIndex(index: number, item: any) {
    return index;
  }

  toggleAssessment(editionEnabled: any) {
    if (editionEnabled) {
      this.assessmentGeneralForm.controls.title.enable();
      this.assessmentGeneralForm.controls.description.enable();
      this.assessmentGeneralForm.controls.minScore.enable();
      this.assessmentGeneralForm.controls.maxScore.enable();
    } else {
      this.assessmentGeneralForm.controls.title.disable();
      this.assessmentGeneralForm.controls.description.disable();
      this.assessmentGeneralForm.controls.minScore.disable();
      this.assessmentGeneralForm.controls.maxScore.disable();
    }
  }

  addCriteria() {
    const newId = this.assessment.criterias.length === 0 ? 0 : 1 + this.assessment.criterias.map((c) => c.id).reduce((a, b) => Math.max(a, b));
    const path = `information.assessment.criterias[${this.assessment.criterias.length}]`;
    const value = new AssessmentCriteria(newId, this.newCriteriaTitle, '');
    this._courseApiService.patchCourse(this.course.id, { op: 'add', path, value }).subscribe((course) => {
      this.assessment = course.information.assessment;
      this.newCriteriaTitle = '';
    });
  }

  canAddCriteria() {
    return this.assessment.criterias.length < 6;
  }

  canDeleteCriteria() {
    return this.assessment.criterias.length > 1;
  }

  removeCriteria(index: number) {
    const path = `information.assessment.criterias[${index}]`;
    this._courseApiService.patchCourse(this.course.id, { op: 'remove', path }).subscribe((course) => (this.assessment = course.information.assessment));
  }

  criteriaChanged(event: { data: AssessmentCriteria; index: number }) {
    const path = `information.assessment.criterias[${event.index}]`;
    this._courseApiService.patchCourse(this.course.id, { op: 'replace', path, value: event.data }).subscribe((course) => (this.assessment = course.information.assessment));
  }

  openAssessmentDashboard(event: any) {
    const dialog = this._dialog.open(AssessmentReportDialogComponent, {
      width: 'min(80vw, 1200px)',
      height: 'min(85vh, 800px)',
      panelClass: ['learnspace-theme', 'assessment-report-dialog'],
      data: { assessment: event.assessmentResult, course: event.course },
      autoFocus: false
    });
    dialog.componentInstance.onExplore.subscribe(event => this.onOpenSessionLog(event));
  }

  onOpenSessionLog(event: any) {
    const dialog = this._dialog.open(ProgramReportingSessionLogComponent, {
      width: 'min(80vw, 1200px)',
      maxHeight: 'min(85vh, 800px)',
      panelClass: 'learnspace-theme',
      data: { sessionId: event.sessionId, course: event.course },
      autoFocus: false
    });
    dialog.componentInstance.onExplore.subscribe(event => this.openAssessmentDashboard(event));
  }
}
